import { type Ref, ref } from "vue";

import PermissionsService, { GrantedPermissions } from "./PermissionsService";

/**
 * Instance of this class represents allowed actions on a resource.
 */
export class AccessService {
    private static permissions: Ref<GrantedPermissions> = ref(this.getCachedPermissions());
    private resourcePermissions: GrantedPermissions[string];

    constructor(public resource: string) {
        this.resourcePermissions = AccessService.permissions.value[resource] ?? [];
    }

    permits(action: string): boolean {
        return this.resourcePermissions.includes(action) || import.meta.env.VITE_ENV !== "production";
    }

    canRead(): boolean {
        return this.permits("read");
    }

    canEdit(): boolean {
        return this.permits("edit");
    }

    canDelete(): boolean {
        return this.permits("delete") || this.permits("destroy");
    }

    static async loadPermissions(): Promise<void> {
        try {
            this.setPermissions(await PermissionsService.getPermissions());
        } catch (e) {
            console.error(e);
            console.warn("using cached permissions");
            this.permissions.value = this.getCachedPermissions();
        }
    }

    static setPermissions(permissions: GrantedPermissions): void {
        this.permissions.value = permissions;

        // cache in case future refresh fails
        localStorage.setItem("permissions", JSON.stringify(this.permissions.value));
    }

    private static getCachedPermissions(): GrantedPermissions {
        try {
            return JSON.parse(localStorage.getItem("permissions") || "{}");
        } catch {
            return {};
        }
    }
}
